<template>
  <v-main class="theme-settings-page">
    <div class="theme-settings-page__container">
      <button
        class="theme-settings-page__back"
        type="button"
        @click="goBack"
      >
        <v-icon small>mdi-arrow-left</v-icon>
        Назад
      </button>
      <header class="theme-settings-page__header">
        <div>
          <p class="theme-settings-page__eyebrow">Оформлення</p>
          <h1>Налаштування теми</h1>
          <p>
            Оберіть, як змінюватиметься оформлення вашого шкільного простору.
          </p>
        </div>
        <v-icon class="theme-settings-page__header-icon" aria-hidden="true">
          mdi-palette-outline
        </v-icon>
      </header>

      <v-progress-linear
        v-if="initialLoading"
        indeterminate
        class="theme-settings-page__progress theme-loader"
      />

      <div
        v-if="loadError && !initialLoading"
        class="theme-settings-page__alert theme-settings-page__alert--error"
        role="alert"
      >
        <v-icon color="error">mdi-alert-circle-outline</v-icon>
        <span>Не вдалося завантажити налаштування.</span>
        <button type="button" @click="reload">Спробувати ще раз</button>
      </div>

      <template v-if="form">
        <section class="theme-settings-card" aria-labelledby="dynamic-heading">
          <div class="theme-settings-card__heading">
            <div>
              <h2 id="dynamic-heading">Сезонне оформлення</h2>
              <p>Автоматично змінювати атмосферне оформлення протягом року.</p>
            </div>
            <v-switch
              v-model="form.dynamic_themes_enabled"
              :disabled="saving || !globalEnabled"
              inset
              hide-details
              aria-label="Увімкнути сезонне оформлення"
            />
          </div>

          <div
            v-if="!globalEnabled"
            class="theme-settings-card__policy"
            role="status"
          >
            Сезонне оформлення тимчасово вимкнене адміністратором школи.
          </div>
        </section>

        <section
          v-if="showThemePreviews"
          class="theme-settings-card theme-preview-card"
          aria-labelledby="preview-heading"
        >
          <div class="theme-settings-card__heading">
            <div>
              <h2 id="preview-heading">Перевірка місячних тем</h2>
              <p>Тимчасовий preview без збереження у профілі.</p>
            </div>
            <span class="theme-preview-card__badge">Для тестування</span>
          </div>
          <label class="theme-preview-card__field">
            <span>Показати тему</span>
            <select
              v-model="previewSelection"
              :disabled="initialLoading || !themeChoiceEnabled"
              @change="applyMonthlyPreview"
            >
              <option
                v-for="option in previewOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </section>

        <section
          v-if="showThemePreviews"
          class="theme-settings-card theme-preview-card"
          aria-labelledby="holiday-preview-heading"
        >
          <div class="theme-settings-card__heading">
            <div>
              <h2 id="holiday-preview-heading">
                Перевірка святкових дудлів
              </h2>
              <p>
                Дудл накладається на обрану місячну тему без збереження.
              </p>
            </div>
            <span class="theme-preview-card__badge">Для тестування</span>
          </div>
          <label class="theme-preview-card__field">
            <span>Показати дудл</span>
            <select
              v-model="holidayPreviewSelection"
              :disabled="initialLoading || !themeChoiceEnabled"
              @change="applyHolidayPreview"
            >
              <option
                v-for="option in holidayPreviewOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </section>

        <section
          class="theme-settings-card"
          :class="{ 'theme-settings-card--disabled': !themeChoiceEnabled }"
          aria-labelledby="mode-heading"
        >
          <div class="theme-settings-card__heading">
            <div>
              <h2 id="mode-heading">Режим теми</h2>
              <p>Автоматичний режим враховує поточну пору року.</p>
            </div>
          </div>

          <v-radio-group
            v-model="form.theme_mode"
            :disabled="saving || !themeChoiceEnabled"
            class="theme-mode-grid"
            hide-details
            aria-labelledby="mode-heading"
          >
            <label
              v-for="mode in themeModes"
              :key="mode.value"
              class="theme-mode-option"
              :class="{
                'theme-mode-option--selected': form.theme_mode === mode.value,
              }"
            >
              <v-radio :value="mode.value" />
              <span class="theme-mode-option__icon" aria-hidden="true">
                {{ mode.icon }}
              </span>
              <span>
                <strong>{{ mode.label }}</strong>
                <small>{{ mode.description }}</small>
              </span>
            </label>
          </v-radio-group>
        </section>

        <section
          class="theme-settings-card"
          :class="{ 'theme-settings-card--disabled': !detailsEnabled }"
          aria-labelledby="details-heading"
        >
          <div class="theme-settings-card__heading">
            <div>
              <h2 id="details-heading">Деталі оформлення</h2>
              <p>Виберіть додаткові зміни, які хочете бачити.</p>
            </div>
          </div>

          <div class="theme-setting-list">
            <div
              v-for="setting in detailSettings"
              :key="setting.key"
              class="theme-setting-row"
            >
              <v-icon class="theme-setting-row__icon" aria-hidden="true">
                {{ setting.icon }}
              </v-icon>
              <div class="theme-setting-row__copy">
                <strong>{{ setting.label }}</strong>
                <span>{{ setting.description }}</span>
              </div>
              <v-switch
                v-model="form[setting.key]"
                :disabled="saving || !detailsEnabled"
                inset
                hide-details
                :aria-label="setting.label"
              />
            </div>
          </div>
        </section>

        <footer class="theme-settings-actions">
          <div class="theme-settings-actions__status" aria-live="polite">
            <span v-if="saveError" class="theme-settings-actions__error">
              <v-icon small color="error">mdi-alert-circle-outline</v-icon>
              Не вдалося зберегти зміни. Спробуйте ще раз.
            </span>
            <span v-else-if="saved" class="theme-settings-actions__success">
              <v-icon small color="success">mdi-check-circle-outline</v-icon>
              Налаштування збережено
            </span>
          </div>
          <v-btn
            class="theme-settings-actions__save"
            large
            :loading="saving"
            :disabled="saving || initialLoading || !hasChanges"
            @click="save"
          >
            Зберегти зміни
          </v-btn>
        </footer>
      </template>
    </div>
  </v-main>
</template>

<script>
const WRITABLE_FIELDS = [
  "dynamic_themes_enabled",
  "monthly_changes_enabled",
  "holiday_themes_enabled",
  "animations_enabled",
  "seasonal_mascot_enabled",
  "theme_mode",
];

function editablePreferences(preferences) {
  return WRITABLE_FIELDS.reduce((result, field) => {
    result[field] = preferences[field];
    return result;
  }, {});
}

export default {
  name: "ThemeSettingsPage",
  data() {
    return {
      form: null,
      baseline: null,
      saving: false,
      saved: false,
      saveError: null,
      restoring: false,
      previewSelection: "",
      holidayPreviewSelection: "",
      previewOptions: [
        { value: "", label: "Поточна тема за календарем" },
        { value: "january", label: "Січень — тиха зима" },
        { value: "february", label: "Лютий — крокуси в снігу" },
        { value: "march", label: "Березень — тала вода і верба" },
        { value: "april", label: "Квітень — дощ і магнолія" },
        { value: "may", label: "Травень — свіжа зелень" },
        { value: "june", label: "Червень — початок літа" },
        { value: "july", label: "Липень — сонячний спокій" },
        {
          value: "august",
          label: "Серпень — соняшники і вечірнє небо",
        },
        { value: "september", label: "Вересень — новий навчальний рік" },
        { value: "october", label: "Жовтень — осіннє листя" },
        {
          value: "november",
          label: "Листопад — ліс і вівсяне печиво",
        },
        { value: "december", label: "Грудень — очікування свята" },
      ],
      holidayPreviewOptions: [
        { value: "", label: "Поточний дудл за календарем" },
        { value: "none", label: "Без дудла" },
        { value: "knowledge-day", label: "День знань" },
        { value: "educators-day", label: "День працівників освіти" },
        { value: "saint-nicholas-day", label: "День святого Миколая" },
        { value: "winter-holiday", label: "Різдвяно-зимові свята" },
        { value: "easter", label: "Великдень" },
        { value: "vyshyvanka-day", label: "День вишиванки" },
        { value: "last-bell", label: "Останній дзвоник" },
        { value: "independence-day", label: "День Незалежності" },
      ],
      themeModes: [
        {
          value: "auto",
          label: "Автоматично",
          icon: "✨",
          description: "За календарем",
        },
        {
          value: "classic",
          label: "Класична",
          icon: "◻️",
          description: "Без сезонних змін",
        },
        {
          value: "spring",
          label: "Весна",
          icon: "🌱",
          description: "Свіжі весняні кольори",
        },
        {
          value: "summer",
          label: "Літо",
          icon: "☀️",
          description: "Тепле літнє оформлення",
        },
        {
          value: "autumn",
          label: "Осінь",
          icon: "🍂",
          description: "Затишні осінні кольори",
        },
        {
          value: "winter",
          label: "Зима",
          icon: "❄️",
          description: "Зимова атмосфера",
        },
      ],
      detailSettings: [
        {
          key: "monthly_changes_enabled",
          label: "Місячні зміни",
          description: "Невеликі зміни оформлення щомісяця",
          icon: "mdi-calendar-month-outline",
        },
        {
          key: "holiday_themes_enabled",
          label: "Святкові теми",
          description: "Спеціальне оформлення у святкові дні",
          icon: "mdi-party-popper",
        },
        {
          key: "animations_enabled",
          label: "Анімації",
          description: "Легкі декоративні рухи та ефекти",
          icon: "mdi-auto-fix",
        },
        {
          key: "seasonal_mascot_enabled",
          label: "Сезонний маскот",
          description: "Сезонний образ шкільного персонажа",
          icon: "mdi-emoticon-outline",
        },
      ],
    };
  },
  computed: {
    showThemePreviews() {
      return (
        this.$store.getters["ConfigStore/isStage"] ||
        this.$store.getters["ConfigStore/isLocal"]
      );
    },
    preferences() {
      return this.$store.getters["ThemeStore/getPreferences"];
    },
    initialized() {
      return this.$store.getters["ThemeStore/isInitialized"];
    },
    initialLoading() {
      return !this.initialized && this.$store.getters["ThemeStore/isLoading"];
    },
    preferenceError() {
      return this.$store.getters["ThemeStore/getErrors"].preferences;
    },
    loadError() {
      return !this.saveError && this.preferenceError;
    },
    globalEnabled() {
      return this.preferences.global_dynamic_themes_enabled !== false;
    },
    themeChoiceEnabled() {
      return this.globalEnabled && Boolean(this.form?.dynamic_themes_enabled);
    },
    detailsEnabled() {
      return this.themeChoiceEnabled && this.form?.theme_mode !== "classic";
    },
    hasChanges() {
      return Boolean(
        this.form &&
          this.baseline &&
          JSON.stringify(this.form) !== JSON.stringify(this.baseline)
      );
    },
    previousRoute() {
      return this.$store.getters["ConfigStore/getPreviousRoute"];
    },
    hasPreviousRoute() {
      return Boolean(
        this.previousRoute?.name &&
          this.previousRoute.name !== this.$route.name &&
          this.previousRoute.fullPath
      );
    },
  },
  watch: {
    preferences: {
      deep: true,
      immediate: true,
      handler(preferences) {
        if (!this.saving) this.restoreForm(preferences);
      },
    },
    form: {
      deep: true,
      handler() {
        if (this.restoring) return;
        this.saved = false;
        this.saveError = null;
      },
    },
  },
  async mounted() {
    if (!this.initialized) {
      await this.$store.dispatch("ThemeStore/initialize");
    }
    this.restoreForm(this.preferences);
    this.previewSelection =
      this.$store.getters["ThemeStore/getPreviewMonthlyCode"] || "";
    this.holidayPreviewSelection =
      this.$store.getters["ThemeStore/getPreviewHolidayCode"] || "";
  },
  methods: {
    goBack() {
      if (this.hasPreviousRoute) {
        this.$router.replace(this.previousRoute.fullPath);
        return;
      }

      this.$router.replace({
        name: this.$store.getters["ConfigStore/getRouteAfterLogin"],
      });
    },
    async applyMonthlyPreview() {
      await this.$store.dispatch(
        "ThemeStore/setMonthlyPreview",
        this.previewSelection
      );
    },
    async applyHolidayPreview() {
      await this.$store.dispatch(
        "ThemeStore/setHolidayPreview",
        this.holidayPreviewSelection
      );
    },
    restoreForm(preferences) {
      this.restoring = true;
      const values = editablePreferences(preferences);
      this.form = { ...values };
      this.baseline = { ...values };
      this.$nextTick(() => {
        this.restoring = false;
      });
    },
    async reload() {
      await this.$store.dispatch("ThemeStore/initialize");
      this.restoreForm(this.preferences);
    },
    async save() {
      if (!this.hasChanges) return;
      this.saving = true;
      this.saved = false;
      this.saveError = null;
      try {
        const preferences = await this.$store.dispatch(
          "ThemeStore/updatePreferences",
          editablePreferences(this.form)
        );
        this.restoreForm(preferences);
        this.saved = true;
      } catch (error) {
        this.saveError = error;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.theme-settings-page {
  background: var(--theme-app-background-accent);
  min-height: 100vh;
}

.theme-settings-page__container {
  margin: 0 auto;
  max-width: 980px;
  padding: 40px 28px 64px;
  width: 100%;
}

.theme-settings-page__back {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--theme-header-background);
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-weight: 700;
  gap: 6px;
  margin: 0 0 18px -6px;
  min-height: 36px;
  padding: 6px;
}

.theme-settings-page__back:hover {
  color: var(--theme-banner-text);
}

.theme-settings-page__back:focus-visible {
  border-radius: 8px;
  outline: 3px solid var(--semantic-focus);
  outline-offset: 2px;
}

.theme-settings-page__back::v-deep .v-icon {
  color: currentColor;
}

.theme-settings-page__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 28px;
}

.theme-settings-page__header h1 {
  color: var(--text-primary);
  font-size: 32px;
  line-height: 1.2;
  margin: 0 0 8px;
}

.theme-settings-page__header p {
  color: var(--text-secondary);
  margin: 0;
}

.theme-settings-page__eyebrow {
  color: var(--theme-accent-decorative) !important;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 7px !important;
  text-transform: uppercase;
}

.theme-settings-page__header-icon {
  background: var(--theme-surface-soft);
  border-radius: 20px;
  color: var(--theme-accent-decorative) !important;
  font-size: 38px !important;
  padding: 18px;
}

.theme-settings-page__progress {
  margin-bottom: 20px;
}

.theme-settings-page__alert {
  align-items: center;
  border-radius: 12px;
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 14px 16px;
}

.theme-settings-page__alert--error {
  background: rgba(255, 82, 82, 0.1);
  color: var(--semantic-error);
}

.theme-settings-page__alert button {
  color: inherit;
  font-weight: 700;
  margin-left: auto;
  text-decoration: underline;
}

.theme-settings-card {
  background: var(--surface-primary);
  border: 1px solid var(--theme-border-decorative);
  border-radius: 18px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  margin-bottom: 20px;
  padding: 24px;
  transition: opacity 0.2s ease;
}

.theme-settings-card--disabled {
  opacity: 0.58;
}

.theme-settings-card__heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.theme-settings-card h2 {
  color: var(--text-primary);
  font-size: 20px;
  margin: 0 0 4px;
}

.theme-settings-card p {
  color: var(--text-secondary);
  margin: 0;
}

.theme-settings-card__policy {
  background: var(--theme-surface-soft);
  border-radius: 10px;
  color: var(--text-secondary);
  margin-top: 18px;
  padding: 12px 14px;
}

.theme-preview-card__badge {
  background: var(--theme-surface-decorative);
  border-radius: 999px;
  color: var(--theme-banner-text);
  font-size: 11px;
  font-weight: 800;
  padding: 6px 10px;
}

.theme-preview-card__field {
  display: block;
  margin-top: 18px;
}

.theme-preview-card__field > span {
  color: var(--text-secondary);
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 6px;
}

.theme-preview-card__field select {
  background: var(--theme-surface-soft);
  border: 1px solid var(--theme-accent-decorative);
  border-radius: 10px;
  color: var(--theme-banner-text);
  font: inherit;
  min-height: 46px;
  padding: 0 12px;
  width: 100%;
}

.theme-preview-card__field select:focus-visible {
  outline: 3px solid var(--theme-accent-decorative);
  outline-offset: 2px;
}

.theme-mode-grid {
  margin-top: 20px;
}

.theme-mode-grid::v-deep .v-input--radio-group__input {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.theme-mode-option {
  align-items: center;
  border: 1px solid var(--theme-border-decorative);
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  min-height: 78px;
  padding: 12px;
}

.theme-mode-option--selected {
  background: var(--theme-surface-decorative);
  border-color: var(--theme-accent-decorative);
}

.theme-settings-page::v-deep
  .v-input--switch.v-input--is-label-active:not(.v-input--is-disabled)
  .v-input--switch__track {
  color: var(--theme-accent-decorative) !important;
  opacity: 0.58;
}

.theme-settings-page::v-deep
  .v-input--switch.v-input--is-label-active:not(.v-input--is-disabled)
  .v-input--switch__thumb,
.theme-settings-page::v-deep .v-radio.v-item--active .v-icon {
  color: var(--theme-header-background) !important;
}

.theme-mode-option__icon {
  font-size: 24px;
  margin: 0 10px 0 2px;
}

.theme-mode-option strong,
.theme-mode-option small {
  display: block;
}

.theme-mode-option strong {
  color: var(--text-primary);
}

.theme-mode-option small {
  color: var(--text-secondary);
  margin-top: 2px;
}

.theme-setting-list {
  margin-top: 12px;
}

.theme-setting-row {
  align-items: center;
  border-top: 1px solid var(--theme-border-decorative);
  display: flex;
  min-height: 78px;
}

.theme-setting-row:first-child {
  border-top: 0;
}

.theme-setting-row__icon {
  color: var(--theme-accent-decorative) !important;
  margin-right: 16px;
}

.theme-setting-row__copy {
  flex: 1;
  min-width: 0;
}

.theme-setting-row__copy strong,
.theme-setting-row__copy span {
  display: block;
}

.theme-setting-row__copy strong {
  color: var(--text-primary);
}

.theme-setting-row__copy span {
  color: var(--text-secondary);
  font-size: 14px;
  margin-top: 2px;
}

.theme-settings-actions {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-top: 4px;
}

.theme-settings-actions__status {
  min-height: 24px;
}

.theme-settings-actions__save:not(.v-btn--disabled) {
  background-color: var(--theme-header-background) !important;
  color: var(--theme-header-text) !important;
}

.theme-settings-actions__save:not(.v-btn--disabled):hover {
  filter: brightness(0.9);
}

.theme-settings-actions__error,
.theme-settings-actions__success {
  align-items: center;
  display: flex;
  gap: 6px;
}

.theme-settings-actions__error {
  color: var(--semantic-error);
}

.theme-settings-actions__success {
  color: var(--semantic-success);
}

@media (max-width: 700px) {
  .theme-settings-page__container {
    padding: 28px 16px 48px;
  }

  .theme-settings-page__header h1 {
    font-size: 27px;
  }

  .theme-settings-page__header-icon {
    display: none;
  }

  .theme-mode-grid::v-deep .v-input--radio-group__input {
    grid-template-columns: 1fr;
  }

  .theme-settings-card {
    padding: 20px 16px;
  }

  .theme-settings-actions {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }

  .theme-settings-actions .v-btn {
    width: 100%;
  }
}
</style>
